/**
 * The RLHF feedback vocabulary — the typed data a human review produces.
 *
 * Generalized from the career studio's production-proven `annotation.ts` (the
 * W3C TextQuoteSelector anchor + the 8 annotation kinds + 5 severities): the one
 * consumer-specific axis — the **quality gate** an annotation is tagged with, and
 * the **rubric dimension** a grade scores — is left OPEN. The ArkType validators
 * treat both as plain `string` (any consumer vocabulary is accepted at runtime);
 * the `*Of<…>` type aliases narrow that same shape to a consumer's literal union
 * for compile-time safety (`AnnotationOf<CareerGate>`, `DimensionGradeOf<"D1"|…>`).
 * This mirrors elicitation-core's "generic over the consumer's construct-target"
 * split: the primitive vocabulary is shared, only the join key varies.
 *
 * Everything here is data — ArkType shapes and inferred types, no I/O. Model /
 * human input is VALIDATED with these, never cast (platform trust-boundary rule).
 */

import { type } from "arktype"

/**
 * A robust pointer to a reviewed span, following the W3C Web Annotation
 * TextQuoteSelector: `exact` quote plus `prefix`/`suffix` context so the mark
 * re-anchors after the text is edited (offsets shift, quotes usually survive).
 * `start`/`end` are a best-effort character-offset fallback.
 */
export const TextAnchor = type({
  /** The exact selected text. Primary anchor. */
  exact: "string >= 1",
  /** Up to ~32 chars before the selection, to disambiguate repeated quotes. */
  "prefix?": "string",
  /** Up to ~32 chars after the selection. */
  "suffix?": "string",
  /** Character offset into the subject's rendered text — fallback only. */
  "start?": "number >= 0",
  "end?": "number >= 0",
})
export type TextAnchor = typeof TextAnchor.infer

/** What kind of review signal this is. Drives colour, the prompt verb, and OKF framing. */
export const AnnotationKind = type(
  "'issue' | 'praise' | 'question' | 'rewrite-request' | 'claim-doubt' | 'slop-flag' | 'evidence-gap' | 'factual-correction'",
)
export type AnnotationKind = typeof AnnotationKind.infer

/** How much this matters. `positive` is the REWARD signal (keep doing this). */
export const Severity = type("'blocker' | 'major' | 'minor' | 'nit' | 'positive'")
export type Severity = typeof Severity.infer

/** The annotation kinds as an ordered list (for pickers). Kept in sync via `satisfies`. */
export const ANNOTATION_KINDS = [
  "issue",
  "praise",
  "question",
  "rewrite-request",
  "claim-doubt",
  "slop-flag",
  "evidence-gap",
  "factual-correction",
] as const satisfies readonly AnnotationKind[]

/** The severities as an ordered list (for pickers). Kept in sync via `satisfies`. */
export const SEVERITIES = ["blocker", "major", "minor", "nit", "positive"] as const satisfies readonly Severity[]

/** A 1–5 rubric score — a bounded integer (a judge or an operator picks one). */
export const Score = type("1 <= number.integer <= 5")
export type Score = typeof Score.infer

/**
 * A draft annotation as a reviewer creates it, before the session assigns id /
 * timestamp / resolution. `gate` is the consumer's open vocabulary (validated as
 * a string; narrow with {@link AnnotationDraftOf}). `subjectRef` names WHAT is
 * annotated (a generation, a paragraph, an artifact) — generalized from the
 * career annotation's `artifactId`.
 */
export const AnnotationDraft = type({
  /** The subject being annotated, by opaque ref (e.g. `fx-startup-backend::grok-4.5`). */
  subjectRef: "string >= 1",
  anchor: TextAnchor,
  kind: AnnotationKind,
  severity: Severity,
  /** Consumer-supplied gate vocabulary (e.g. career's `evidence | anti-slop | …`). */
  gate: "string",
  /** The reviewer's note — what is wrong/right and why. */
  comment: "string >= 1",
  /** Optional concrete replacement text the reviewer wants instead (the strongest signal). */
  "suggestedRewrite?": "string",
  /** Optional pointer into an evidence ledger backing the note. */
  "evidenceRef?": "string",
})
export type AnnotationDraft = typeof AnnotationDraft.infer
/** {@link AnnotationDraft} narrowed to a consumer's literal gate union. */
export type AnnotationDraftOf<Gate extends string> = Omit<AnnotationDraft, "gate"> & { gate: Gate }

/**
 * One folded annotation — a draft plus the identity the session assigned. `resolved`
 * flips once a later iteration addresses it, so the loop can measure closure.
 */
export const Annotation = AnnotationDraft.merge({
  id: "string >= 1",
  resolved: "boolean",
  createdAt: "string.date.iso",
  /** Who made the mark (a username / operator id — never a raw token). */
  actor: "string >= 1",
})
export type Annotation = typeof Annotation.infer
/** {@link Annotation} narrowed to a consumer's literal gate union. */
export type AnnotationOf<Gate extends string> = Omit<Annotation, "gate"> & { gate: Gate }

/** The 1/3/5 anchor descriptions of one rubric dimension (the gradeable bar). */
export const RubricAnchors = type({
  "1": "string >= 1",
  "3": "string >= 1",
  "5": "string >= 1",
})
export type RubricAnchors = typeof RubricAnchors.infer

/**
 * One rubric dimension a subject is graded on. `kind` distinguishes a **gate**
 * dimension (a 1 is a hard fail) from a **quality** dimension (profiles the
 * subject). `id` is the consumer's open dimension vocabulary (e.g. `D1`…`D9`).
 */
export const RubricDimension = type({
  id: "string >= 1",
  kind: "'gate' | 'quality'",
  title: "string >= 1",
  anchors: RubricAnchors,
})
export type RubricDimension = typeof RubricDimension.infer
/** {@link RubricDimension} narrowed to a consumer's literal dimension union. */
export type RubricDimensionOf<Dimension extends string> = Omit<RubricDimension, "id"> & { id: Dimension }

/** One operator grade of one dimension on one subject — a 1–5 score with a reason. */
export const DimensionGrade = type({
  subjectRef: "string >= 1",
  /** Consumer-supplied dimension id (e.g. `D3`). */
  dimension: "string >= 1",
  score: Score,
  rationale: "string >= 1",
  /** Optional verbatim span the grade is anchored to. */
  "quote?": "string",
})
export type DimensionGrade = typeof DimensionGrade.infer
/** {@link DimensionGrade} narrowed to a consumer's literal dimension union. */
export type DimensionGradeOf<Dimension extends string> = Omit<DimensionGrade, "dimension"> & { dimension: Dimension }

/**
 * An EXTERNAL judge's score of one dimension — reference data shown next to the
 * operator's own grade for calibration (disagreement highlighting). Not authored
 * in the session; it comes from a judge panel (e.g. the bench's LLM judges).
 */
export const JudgeVerdictRef = type({
  /** The judge model label (e.g. `sonnet-5`, `opus-4.8`). */
  judgeModel: "string >= 1",
  dimension: "string >= 1",
  score: Score,
  rationale: "string >= 1",
  "quote?": "string",
})
export type JudgeVerdictRef = typeof JudgeVerdictRef.infer
/** {@link JudgeVerdictRef} narrowed to a consumer's literal dimension union. */
export type JudgeVerdictRefOf<Dimension extends string> = Omit<JudgeVerdictRef, "dimension"> & { dimension: Dimension }

/** A head-to-head preference between two subjects — a paired-comparison signal. */
export const PairwiseChoice = type({
  leftRef: "string >= 1",
  rightRef: "string >= 1",
  winner: "'left' | 'right' | 'tie'",
  rationale: "string >= 1",
})
export type PairwiseChoice = typeof PairwiseChoice.infer

/** An operator's decision on an open taste question (canon §5-style calibration). */
export const TasteRuling = type({
  questionId: "string >= 1",
  ruling: "string >= 1",
  "notes?": "string",
})
export type TasteRuling = typeof TasteRuling.infer
